import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { ChatWithDetails, ChatParticipant, Profile } from '../types';

/**
 * Hook to fetch a single chat's details including participants
 */
export const useChat = (chatId?: string, userId?: string) => {
  const [chat, setChat] = useState<ChatWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;

    const fetchChat = async (): Promise<boolean> => {
      if (!chatId || !userId) {
        setLoading(false);
        return true;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch chat with participants - use maybeSingle to avoid error on new chats
        const { data: chatData, error: chatError } = await supabase
          .from('chats')
          .select(`
            *,
            participants:chat_participants(*)
          `)
          .eq('id', chatId)
          .maybeSingle();

        if (chatError) throw chatError;
        
        // If no chat found, retry after a short delay (race condition with new chats)
        if (!chatData) {
          if (retryCount < maxRetries) {
            retryCount++;
            console.log(`Chat not found yet, retrying (${retryCount}/${maxRetries})...`);
            return false; // Signal to retry
          }
          throw new Error('Chat not found');
        }

        // Get all user IDs from participants
        const userIds = chatData.participants?.map((p: any) => p.user_id).filter(Boolean) || [];

        // Fetch profiles for all participants
        let profiles: Profile[] = [];
        if (userIds.length > 0) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, full_name, nickname, avatar_url, email, is_dependent')
            .in('id', userIds);

          if (profileError) {
            console.error('Error fetching profiles:', profileError);
          }
          profiles = profileData || [];
        }

        // Create profile map for quick lookup
        const profileMap = new Map<string, Profile>(
          profiles.map((p) => [p.id, p])
        );

        // Attach profile data to participants
        const participantsWithUsers: ChatParticipant[] = (chatData.participants || []).map((p: any) => ({
          ...p,
          user: profileMap.get(p.user_id) || null,
        }));

        const chatWithDetails: ChatWithDetails = {
          ...chatData,
          participants: participantsWithUsers,
          unread_count: 0,
        };

        setChat(chatWithDetails);
        return true; // Success
      } catch (err) {
        console.error('Error fetching chat:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch chat');
        return true; // Don't retry on actual errors
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch with retry logic for new chats
    const attemptFetch = async () => {
      const success = await fetchChat();
      if (!success && retryCount < maxRetries) {
        setTimeout(attemptFetch, 500); // Retry after 500ms
      }
    };

    attemptFetch();
  }, [chatId, userId]);

  /**
   * Get the other participant in a direct chat
   */
  const getOtherParticipant = (): ChatParticipant | null => {
    if (!chat || chat.type !== 'direct') return null;
    return chat.participants?.find((p) => p.user_id !== userId) || null;
  };

  /**
   * Get display name for the chat
   */
  const getChatName = (): string => {
    if (!chat) return '';

    if (chat.type === 'direct') {
      const other = getOtherParticipant();
      return other?.user?.nickname || other?.user?.full_name || 'Unknown User';
    }

    return chat.name || 'Group Chat';
  };

  /**
   * Get avatar URL for the chat
   */
  const getChatAvatar = (): string | null => {
    if (!chat) return null;

    if (chat.type === 'direct') {
      const other = getOtherParticipant();
      return other?.user?.avatar_url || null;
    }

    return chat.avatar_url || null;
  };

  return {
    chat,
    loading,
    error,
    getOtherParticipant,
    getChatName,
    getChatAvatar,
  };
};

